import RoleRequired from '@/common/components/RoleRequired.tsx';
import { authClient } from '@/lib/auth-client.ts';
import {
  AppShell,
  Box,
  Button,
  Divider,
  Group,
  type MantineBreakpoint,
  Menu,
  Modal,
  Stack,
  Text,
  UnstyledButton,
  useComputedColorScheme,
} from '@mantine/core';
import {
  BriefcaseIcon,
  BuildingIcon,
  BuildingsIcon,
  CurrencyDollarIcon,
  FileTextIcon,
} from '@phosphor-icons/react';
import * as Sentry from '@sentry/react';
import {
  Link,
  useMatches,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import PermissionRequired from '@/common/components/PermissionRequired.tsx';
import AuthRequired from '@/common/components/AuthRequired.tsx';
import { getBuildInfoControllerGetBuildInfoQueryOptions } from '@/generated/api/build-info.ts';
import { useDisclosure, useIdle, useLocalStorage } from '@mantine/hooks';
import ReactMarkdown from 'react-markdown';
import useBannerAlert from '@/common/hooks/useBannerAlert.tsx';
import BannerAlert from '@/common/components/BannerAlert.tsx';
import { NotificationButton } from '@/common/notifications/NavbarNotificationButton.tsx';
import ImpersonationBanner from '@/common/components/app-shell/ImpersonationBanner.tsx';
// import UserMenu from '@/common/components/app-shell/UserMenu.tsx';

type Sidebar = {
  width?: number;
  breakpoint?: number | MantineBreakpoint;
  children: ReactNode;
};

type RouteAppShellProps = {
  children: ReactNode;
  sidebar?: Sidebar;
  aside?: Sidebar;
};

const APP_SHELL_ALERT_HEIGHT_PX = 28;
const APP_SHELL_IMPERSONATION_HEIGHT_PX = 36;
const APP_SHELL_HEADER_HEIGHT_PX = 48;
const APP_SHELL_FOOTER_HEIGHT_PX = 18;

export default function RouteAppShell({
  children,
  sidebar,
  aside,
}: RouteAppShellProps) {
  const idle = useIdle(60_000, { initialState: false });
  const [lastChangelogVersion, setLastChangelogVersion] = useLocalStorage({
    key: 'last-changelog-version',
    defaultValue: '',
    getInitialValueInEffect: false,
  });
  const [lastSeenVersion, setLastSeenVersion] = useState('');

  const [changelogModal, changelogModalFuncs] = useDisclosure(false);
  const [newVersionModal, newVersionModalFuncs] = useDisclosure(false);

  const navigate = useNavigate();
  const isDark = useComputedColorScheme() === 'dark';
  const matches = useMatches();
  const isAnonymousRoute = useMemo(
    () =>
      matches.some(
        (match) => match.staticData.authorization?.role === 'anonymous',
      ),
    [matches],
  );

  const session = authClient.useSession();
  const serverBuildInfoQuery = useQuery(
    getBuildInfoControllerGetBuildInfoQueryOptions({
      query: {
        enabled: !isAnonymousRoute && Boolean(session.data),
        staleTime: 60_000,
        retry: false,
      },
    }),
  );
  const bannerAlert = useBannerAlert({ enabled: !isAnonymousRoute });
  const impersonationActive =
    !isAnonymousRoute && Boolean(session.data?.session.impersonatedBy);
  const activeBannerHeight =
    (bannerAlert.active ? APP_SHELL_ALERT_HEIGHT_PX : 0) +
    (impersonationActive ? APP_SHELL_IMPERSONATION_HEIGHT_PX : 0);

  useEffect(() => {
    if (!serverBuildInfoQuery.data) return;
    if (lastChangelogVersion === '')
      setLastChangelogVersion(serverBuildInfoQuery.data.version);
    if (lastSeenVersion === '') {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLastSeenVersion(serverBuildInfoQuery.data.version);
    } else {
      if (idle && serverBuildInfoQuery.data.version !== lastSeenVersion) {
        newVersionModalFuncs.open();
      }
    }
  }, [
    serverBuildInfoQuery.data,
    lastChangelogVersion,
    setLastChangelogVersion,
    lastSeenVersion,
    newVersionModalFuncs,
    idle,
  ]);

  const newVersionAvailable = useMemo(() => {
    return (
      serverBuildInfoQuery.data !== undefined &&
      lastChangelogVersion !== '' &&
      serverBuildInfoQuery.data.version !== lastChangelogVersion
    );
  }, [lastChangelogVersion, serverBuildInfoQuery.data]);

  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const liftsActive = pathname.startsWith('/big-rentz-lifts');
  const supportCasesActive = pathname.startsWith(
    '/field-services/support-cases',
  );
  const workOrdersActive = pathname.startsWith('/work-orders');
  const dataActive = pathname.startsWith('/data');
  const assetsActive = pathname.startsWith('/assets');
  const adminActive = pathname.startsWith('/admin');
  const financeActive = pathname.startsWith('/finance');

  return (
    <AppShell
      header={{ height: APP_SHELL_HEADER_HEIGHT_PX }}
      footer={{ height: APP_SHELL_FOOTER_HEIGHT_PX }}
      navbar={
        sidebar
          ? { width: sidebar.width ?? 260, breakpoint: sidebar.breakpoint ?? 0 }
          : undefined
      }
      aside={
        aside
          ? { width: aside.width ?? 260, breakpoint: aside.breakpoint ?? 0 }
          : undefined
      }
      padding={0}
    >
      <AppShell.Header
        style={{
          borderBottom: '1px solid var(--mantine-color-default-border)',
          backgroundColor: 'var(--mantine-color-default)',
        }}
      >
        <AuthRequired>
          <Group h="100%" px="sm" justify="space-between" wrap="nowrap">
            <Group gap="md" wrap="nowrap">
              <Group gap={4} wrap="nowrap">
                <Group gap={0} wrap="nowrap">
                  <Text fw={700} size="sm">
                    P
                  </Text>
                  <Text fw={500} size="xs" c="dimmed">
                    anic
                  </Text>
                </Group>
                <Group gap={0} wrap="nowrap">
                  <Text fw={700} size="sm">
                    A
                  </Text>
                  <Text fw={500} size="xs" c="dimmed">
                    voidance
                  </Text>
                </Group>
                <Group gap={0} wrap="nowrap">
                  <Text fw={700} size="sm">
                    C
                  </Text>
                  <Text fw={500} size="xs" c="dimmed">
                    onsole
                  </Text>
                </Group>
              </Group>
              <Divider orientation="vertical" />
              <Group gap={6} wrap="nowrap">
                <PermissionRequired permission={{ workOrder: ['read'] }}>
                  <Button
                    component={Link}
                    to="/work-orders"
                    variant={workOrdersActive ? 'filled' : 'subtle'}
                    color={workOrdersActive ? 'blue' : 'gray'}
                    size="compact-sm"
                  >
                    Work Orders
                  </Button>
                </PermissionRequired>
                <Button
                  component={Link}
                  to="/field-services/support-cases"
                  variant={supportCasesActive ? 'filled' : 'subtle'}
                  color={supportCasesActive ? 'blue' : 'gray'}
                  size="compact-sm"
                >
                  Support Cases
                </Button>
                <PermissionRequired permission={{ lift: ['read'] }}>
                  <Button
                    component={Link}
                    to="/big-rentz-lifts"
                    variant={liftsActive ? 'filled' : 'subtle'}
                    color={liftsActive ? 'blue' : 'gray'}
                    size="compact-sm"
                  >
                    Lifts
                  </Button>
                </PermissionRequired>
                <PermissionRequired permission={{ asset: ['read'] }}>
                  <Button
                    component={Link}
                    to="/assets"
                    variant={assetsActive ? 'filled' : 'subtle'}
                    color={assetsActive ? 'blue' : 'gray'}
                    size="compact-sm"
                  >
                    Assets
                  </Button>
                </PermissionRequired>
                <PermissionRequired permission={{ salesOrder: ['read'] }}>
                  <Menu trigger="click-hover">
                    <Menu.Target>
                      <Button
                        variant={financeActive ? 'filled' : 'subtle'}
                        color={financeActive ? 'blue' : 'gray'}
                        size="compact-sm"
                      >
                        Finance
                      </Button>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <PermissionRequired permission={{ salesOrder: ['read'] }}>
                        <Menu.Item
                          leftSection={<CurrencyDollarIcon size={16} />}
                          onClick={() =>
                            navigate({ to: '/finance/sales-orders' })
                          }
                        >
                          Sales Orders
                        </Menu.Item>
                      </PermissionRequired>
                    </Menu.Dropdown>
                  </Menu>
                </PermissionRequired>
                <Menu trigger="click-hover">
                  <Menu.Target>
                    <Button
                      variant={dataActive ? 'filled' : 'subtle'}
                      color={dataActive ? 'blue' : 'gray'}
                      size="compact-sm"
                    >
                      Data
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      leftSection={<BuildingsIcon size={16} />}
                      onClick={() => navigate({ to: '/data/clients' })}
                    >
                      Clients
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<BriefcaseIcon size={16} />}
                      onClick={() => navigate({ to: '/data/projects' })}
                    >
                      Projects
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<BuildingIcon size={16} />}
                      onClick={() => navigate({ to: '/data/locations' })}
                    >
                      Locations
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<FileTextIcon size={16} />}
                      onClick={() => navigate({ to: '/data/templates' })}
                    >
                      Templates
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>

                <RoleRequired role="admin">
                  <Button
                    component={Link}
                    to="/admin"
                    variant={adminActive ? 'filled' : 'subtle'}
                    color={adminActive ? 'blue' : 'gray'}
                    size="compact-sm"
                  >
                    Admin
                  </Button>
                </RoleRequired>
              </Group>
            </Group>
            {session.data ? (
              <Group gap="sm">
                <NotificationButton />
                <Divider orientation="vertical" />
                <Button
                  variant="transparent"
                  size="compact-xs"
                  color="red"
                  onClick={async () => {
                    const feedback = Sentry.getFeedback();
                    const form = await feedback?.createForm();
                    form?.appendToDom();
                    form?.open();
                  }}
                >
                  Report a Bug
                </Button>
                <Divider orientation="vertical" />
                {/*<UserMenu name={session.data.user.name} />*/}
                <Button
                  variant="transparent"
                  size="compact-xs"
                  onClick={() => authClient.signOut()}
                >
                  Log Out
                </Button>
              </Group>
            ) : null}
          </Group>
        </AuthRequired>
      </AppShell.Header>

      {sidebar ? (
        <AppShell.Navbar
          p="sm"
          style={{
            borderRight: '1px solid var(--mantine-color-default-border)',
            backgroundColor: 'var(--mantine-color-body)',
          }}
        >
          {sidebar.children}
        </AppShell.Navbar>
      ) : null}

      <AppShell.Main
        style={{
          overflow: 'hidden',
          backgroundColor: isDark ? '#1B1C1F' : 'var(--mantine-color-body)',
        }}
      >
        <Modal
          opened={changelogModal}
          onClose={() => changelogModalFuncs.close()}
          title="Changelog"
          size="xl"
        >
          {serverBuildInfoQuery.data === undefined ? (
            'Loading'
          ) : (
            <ReactMarkdown>{serverBuildInfoQuery.data.changelog}</ReactMarkdown>
          )}
        </Modal>
        <Modal
          opened={newVersionModal}
          onClose={() => newVersionModalFuncs.close()}
          withCloseButton={false}
          title="New Version"
        >
          <Stack gap="md">
            <Text size="sm">
              {serverBuildInfoQuery.data === undefined
                ? 'Loading'
                : 'A new version of PAC is available, please refresh your browser. Any pending work you have will be saved.'}
            </Text>
            <Button fullWidth onClick={() => window.location.reload()}>
              Refresh
            </Button>
          </Stack>
        </Modal>
        {impersonationActive && session.data ? (
          <ImpersonationBanner
            height={APP_SHELL_IMPERSONATION_HEIGHT_PX}
            name={session.data.user.name}
            email={session.data.user.email}
          />
        ) : null}
        <BannerAlert
          height={APP_SHELL_ALERT_HEIGHT_PX}
          enabled={!isAnonymousRoute}
        />
        <Box
          style={{
            height: `calc(100dvh - ${APP_SHELL_HEADER_HEIGHT_PX + APP_SHELL_FOOTER_HEIGHT_PX + activeBannerHeight}px)`,
            minHeight: 0,
          }}
        >
          {children}
        </Box>
      </AppShell.Main>

      {aside ? (
        <AppShell.Aside
          style={{
            borderLeft: '1px solid var(--mantine-color-default-border)',
            backgroundColor: 'var(--mantine-color-body)',
          }}
        >
          {aside.children}
        </AppShell.Aside>
      ) : null}
      <AppShell.Footer m={0} p={0} style={{ borderBottom: '0px' }}>
        <Group gap="xs" justify="end">
          <Text size="xs" c="dimmed">
            {serverBuildInfoQuery.isLoading || !serverBuildInfoQuery.data
              ? 'loading'
              : `${serverBuildInfoQuery.data.version} ${
                  serverBuildInfoQuery.data.branch
                }@${serverBuildInfoQuery.data.commit.slice(0, 7)}`}
          </Text>
          {serverBuildInfoQuery.data && (
            <UnstyledButton
              bg={newVersionAvailable ? 'blue' : 'gray'}
              m={0}
              p={0}
              px="xs"
              onClick={() => {
                setLastChangelogVersion(serverBuildInfoQuery.data.version);
                changelogModalFuncs.open();
              }}
            >
              <Text c="white" fw={700} size="xs">
                {newVersionAvailable ? "What's New" : 'Changelog'}
              </Text>
            </UnstyledButton>
          )}
        </Group>
      </AppShell.Footer>
    </AppShell>
  );
}
